import React, { useContext, useEffect, useState, memo, useCallback } from 'react';
import { isEqual } from 'lodash';
import { TableRow, TableRowColumn } from 'material-ui';
import styles from '../TcIndex/styles';
import { CurrentTypeByPurposeContext, HandleUpdateErrorsContext, VesselDwtTypesContext } from './HiresPage';
import FromTo from './rowData/FromTo';
import LayCan from '../TcIndex/RowData/LayCan';
import HireType from './rowData/HireType';
import HireValue from './rowData/HireValue';
import VesselInfo from './rowData/VesselInfo';
import Durations from '../TcIndex/RowData/Durations';
import FuelPrice from './rowData/FuelPrice';
import HireCargo from './rowData/HireCargo';
import HireCargoRegion from './rowData/HireCargoRegion';
import { Remove } from './rowData/Remove';
import DwtGroup from './rowData/DwtGroup';
import BunkerPlace from './rowData/BunkerPlace';

export const s = {
    small: { ...styles.tableRowSm, height: 'unset' },
    normal: { ...styles.tableRow, height: 'unset' },
};

const placesMap = {
    placeFrom: 'delivery',
    placeTo: 'redelivery',
    regionFrom: 'mainRegionDelivery',
    regionTo: 'mainRegionRedelivery',
};

function HiresTableRow({
    hireData, errors: _errors, rowIndex, changeFieldsMultiple, 
}) {
    const handleUpdateErrors = useContext(HandleUpdateErrorsContext);
    const currentTypeByPurpose = useContext(CurrentTypeByPurposeContext);
    const vesselDwtTypes = useContext(VesselDwtTypesContext);
    const [localHireData, setLocalHireData] = useState(hireData);
    const [errors, setErrors] = useState(_errors);
    const [currentDwtGroups, setCurrentDwtGroups] = useState([]);

    const handleFieldChange = (changes) => {
        changes.forEach(({ field, value }) => {
            setLocalHireData(prev => ({ ...prev, [field]: value }));
        });
        changeFieldsMultiple(changes);
    };

    useEffect(() => {
        if (currentTypeByPurpose || (localHireData.vessel && localHireData.vessel.typeByPurpose)) {
            setCurrentDwtGroups(vesselDwtTypes.filter(d => d.typeByPurpose.includes(currentTypeByPurpose || localHireData.vessel.typeByPurpose)));
        }
    }, [currentTypeByPurpose, localHireData.vessel]);

    useEffect(() => {
        setErrors(_errors[rowIndex]);
    }, [_errors]);

    const handleErrors = useCallback((field, value) => {
        try {
            if (handleUpdateErrors) {
                handleUpdateErrors(rowIndex, field, value);
            }
        } catch (error) {
            console.error('Error in handleUpdateErrors:', error);
        }
    }, [handleUpdateErrors, rowIndex]);

    return (
        <React.Fragment>
            <TableRow key={hireData._id}>
                <VesselInfo
                    vessel={localHireData.vessel}
                    changeFieldsMultiple={handleFieldChange}
                    countIndex={rowIndex}
                    setHasErrors={handleErrors}
                    required={false}
                />
                <TableRowColumn style={{ ...s.normal, ...(errors && errors.vesselDwtGroup ? styles.error : {}) }}>
                    <DwtGroup 
                        vesselType={localHireData.vesselDwtGroup}
                        changeFieldsMultiple={handleFieldChange}
                        countIndex={rowIndex}
                        currentDwtGroups={currentDwtGroups}
                        required
                        setHasErrors={handleErrors}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.normal }}>
                    <FromTo 
                        name={`delivery_${rowIndex}`}
                        field='delivery'
                        data={localHireData.delivery}
                        changeFieldsMultiple={handleFieldChange}
                        countIndex={rowIndex}
                        mainRegions={false}
                        withAreas={false}
                        setHasErrors={handleErrors}
                        checkRequiredMain={false}
                        bunkerPlace={localHireData.bunkerPlace}
                        {...placesMap}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.normal, ...(errors && errors.mainRegionDelivery ? styles.error : {}) }}>
                    <FromTo 
                        name={`mainRegionDelivery_${rowIndex}`}
                        field='mainRegionDelivery'
                        data={localHireData.mainRegionDelivery}
                        changeFieldsMultiple={handleFieldChange}
                        countIndex={rowIndex}
                        setHasErrors={handleErrors}
                        required
                        mainRegions
                        withAreas
                        isInBalticIndex
                        onlyAreas
                        bunkerPlace={localHireData.bunkerPlace}
                        {...placesMap}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.normal }}>
                    <FromTo 
                        name={`redelivery_${rowIndex}`}
                        field='redelivery'
                        data={localHireData.redelivery}
                        changeFieldsMultiple={handleFieldChange}
                        countIndex={rowIndex}
                        mainRegions={false}
                        withAreas={false}
                        setHasErrors={handleErrors}
                        checkRequiredMain={false}
                        {...placesMap}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.normal, ...(errors && errors.mainRegionRedelivery ? styles.error : {}) }}>
                    <FromTo 
                        name={`mainRegionRedelivery_${rowIndex}`}
                        field='mainRegionRedelivery'
                        data={localHireData.mainRegionRedelivery}
                        changeFieldsMultiple={handleFieldChange}
                        countIndex={rowIndex}
                        setHasErrors={handleErrors}
                        required
                        mainRegions
                        withAreas
                        onlyAreas
                        isInBalticIndex
                        {...placesMap}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.normal }}>
                    <LayCan 
                        lay={localHireData.lay}
                        can={localHireData.can}
                        required
                        setHasErrors={handleErrors}
                        countIndex={rowIndex}
                        changeFieldsMultiple={handleFieldChange}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.small, ...(errors && errors.hireType ? styles.error : {}) }}>
                    <HireType 
                        hireType={localHireData.hireType}
                        countIndex={rowIndex}
                        changeFieldsMultiple={handleFieldChange}
                        required
                        setHasErrors={handleErrors}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.small, ...(errors && errors.hire ? styles.error : {}) }}>
                    <HireValue 
                        hire={localHireData.hire}
                        countIndex={rowIndex}
                        setHasErrors={handleErrors}
                        changeFieldsMultiple={handleFieldChange}
                        required
                    />
                </TableRowColumn>
                <FuelPrice
                    fuelPrice={localHireData.fuelPrice}
                    countIndex={rowIndex}
                    changeFieldsMultiple={handleFieldChange}
                    lay={localHireData.lay}
                    bunkerPlace={localHireData.bunkerPlace}
                    mainRegionDelivery={localHireData.mainRegionDelivery}
                />
                <TableRowColumn style={{ ...s.normal }}>
                    <BunkerPlace 
                        name={`bunkerPlace_${rowIndex}`}
                        field='bunkerPlace'
                        data={localHireData.bunkerPlace}
                        changeFieldsMultiple={handleFieldChange}
                        countIndex={rowIndex}
                        setHasErrors={handleErrors}
                        mainRegions
                        withAreas
                        onlyAreas
                        isInBalticIndex
                        lay={localHireData.lay}
                        {...placesMap}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.small }}>
                    <Durations 
                        duration={localHireData.duration}
                        countIndex={rowIndex}
                        changeFieldsMultiple={handleFieldChange}
                        type='number'
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.normal }}>
                    <HireCargo 
                        cargo={localHireData.cargo}
                        countIndex={rowIndex}
                        changeFieldsMultiple={handleFieldChange}
                        cargoType={localHireData.cargoTypeId}
                    />
                </TableRowColumn>
                <TableRowColumn style={{ ...s.normal }}>
                    <HireCargoRegion
                        cargoRegion={localHireData.cargoRegionId}
                        countIndex={rowIndex}
                        changeFieldsMultiple={handleFieldChange}
                    />
                </TableRowColumn>
                <TableRowColumn style={{
                    ...s.small, padding: '0 3px', borderRight: '1px solid #eaeaea', textAlign: 'center', 
                }}
                >
                    <Remove 
                        hireDataId={localHireData._id || localHireData.uniqueId}
                        changeFieldsMultiple={handleFieldChange}
                        countIndex={rowIndex}
                    />
                </TableRowColumn>
            </TableRow>
        </React.Fragment>
    );
}

export default memo(HiresTableRow, (prev, next) => (
    isEqual(prev.hireData, next.hireData) &&
    isEqual(prev.errors, next.errors) &&
    prev.rowIndex === next.rowIndex &&
    prev.changeFieldsMultiple === next.changeFieldsMultiple
));



// WEBPACK FOOTER //
// ./app/components/Hires/HiresTableRow.jsx